import { useApolloClient } from '@apollo/react-hooks';
import XLSX from 'xlsx';
import _ from 'lodash';

import { SYLLABUS_MODE } from 'Domains/syllabus';

import { formatFullSchoolTerm, formatSchoolTerm } from '../utils';
import { MODULE_STATUS, RAW_CONTENT_TYPE, formatWatchedPercent } from '../module';
import {
  getSectionReportForExport,
  getSectionReportAllExamForExport,
  getTKSectionReportForExport,
  getTKSectionReportAllExamForExport,
} from '../../GraphQL/queries/report.query.js';

export const convertStudentReportToAOA = grouppedReports => {
  const quizFullScores = [];

  const studentQuizResult = [
    ...Object.values(grouppedReports).map(grouppedReport => {
      const {
        schoolGrade: { shortName: schoolGrade },
        classNo,
        studentNo,
        classroomUsername,
        firstName,
        lastName,
      } = grouppedReport[0];

      return [
        `${schoolGrade}/${classNo}`,
        studentNo,
        classroomUsername,
        `${firstName} ${lastName}`,
        ..._.flatMap(grouppedReport, report => {
          const {
            courseName,
            sectionName,
            subsectionName,
            quizResult: { fullScore, score, rank },
          } = report;

          if (rank !== 'UNRANK') {
            quizFullScores.push({
              id: `${courseName},${sectionName},${subsectionName}`,
              courseName,
              sectionName,
              subsectionName,
              fullScore,
            });
          }
          // คะแนนเฉลี่ย, คะแนนดีที่สุด, คะแนนทั้งหมด
          return [score, score, score];
        }),
      ];
    }),
  ];
  const uniqQuizFullScores = _.uniqBy(quizFullScores, 'id');
  return { studentQuizResult, uniqQuizFullScores };
};

export const convertOverAllReportToAOA = (reports, isAllExam) => {
  const overAllReport = [];

  reports.forEach(report => {
    const {
      schoolGrade,
      classNo,
      studentNo,
      classroomUsername,
      firstName,
      lastName,
      subsections,
    } = report;

    const rowDetail = [
      `${schoolGrade.shortName}/${classNo}`,
      studentNo,
      classroomUsername,
      `${firstName} ${lastName}`,
    ];

    const filteredSubsections = subsections.filter(
      ({ contentType }) => contentType === RAW_CONTENT_TYPE.QUIZ
    );
    if (isAllExam) {
      const MAX_QUIZ_TIMES = 5;

      filteredSubsections.forEach(({ quizResultList }) => {
        let sumScore = 0;
        let bestScore = 0;

        quizResultList.forEach(({ score }) => {
          sumScore += score;
          bestScore = _.max([bestScore, score]);
        });
        rowDetail.push(quizResultList[0].fullScore, sumScore / quizResultList.length, bestScore);
        for (let i = 0; i < MAX_QUIZ_TIMES; i++) {
          if (i < quizResultList.length) {
            rowDetail.push(quizResultList[i].score);
          } else {
            rowDetail.push('');
          }
        }
      });
    } else {
      filteredSubsections.forEach(({ score }) => {
        rowDetail.push(score, score, score);
      });
    }

    overAllReport.push(rowDetail);
  });

  return overAllReport;
};

// reportContentType can be a single content type or a list, e.g. [Quiz, QuizFeed] for the quiz sheet.
// options.includeWatchedPercent adds a "% ที่ดู" cell right after a video subsection's status
// cell (quiz subsections are never affected). Only used for the video sheet.
export const convertProgressReportToAOA = (reports, reportContentType, options = {}) => {
  const { includeWatchedPercent = false } = options;
  const aoa = [];
  const reportContentTypes = _.castArray(reportContentType);

  reports.forEach(report => {
    const {
      schoolGrade,
      classNo,
      studentNo,
      classroomUsername,
      firstName,
      lastName,
      subsections,
    } = report;

    const rowDetail = [
      `${schoolGrade.shortName}/${classNo}`,
      studentNo,
      classroomUsername,
      `${firstName} ${lastName}`,
    ];

    const filteredSubsections = subsections.filter(({ contentType }) =>
      reportContentTypes.includes(contentType)
    );

    const totalSubsections = filteredSubsections.length;

    // Only a full COMPLETED (watched/scored past the threshold) counts toward the
    // percentage — IN_PROGRESS (◐) is still short of that and is not "done".
    const totalCompleted = filteredSubsections.filter(
      ({ status }) => status === MODULE_STATUS.COMPLETED
    ).length;

    const progressPercentage =
      totalSubsections === 0 ? 0 : (totalCompleted / totalSubsections) * 100;

    rowDetail.push(progressPercentage.toFixed(0));

    filteredSubsections.forEach(({ contentType, status, watchedPercent }) => {
      let statusTh = '';
      if (contentType === RAW_CONTENT_TYPE.VDO) {
        if (status === MODULE_STATUS.COMPLETED) {
          statusTh = 'เรียนจบแล้ว';
        } else if (status === MODULE_STATUS.IN_PROGRESS) {
          statusTh = 'เริ่มเรียนแล้ว (ไม่ถึงเกณฑ์)';
        } else {
          statusTh = 'ยังเรียนไม่จบ';
        }
      } else {
        statusTh = status === MODULE_STATUS.COMPLETED ? 'สอบแล้ว' : 'ยังไม่สอบ';
      }
      rowDetail.push(statusTh);
      if (includeWatchedPercent && contentType === RAW_CONTENT_TYPE.VDO) {
        rowDetail.push(formatWatchedPercent(watchedPercent));
      }
    });

    aoa.push(rowDetail);
  });

  return aoa;
};

const formatQuizOverAllToAOA = (data, isAllExam, mode) => {
  const { sectionInfos, reports } = data;

  const filteredSectionInfos =
    mode === SYLLABUS_MODE.TRADITIONAL
      ? sectionInfos
          .map(sectionInfo => ({
            sectionCode: sectionInfo.sectionCode,
            sectionName: sectionInfo.sectionName,
            subsectionInfos: sectionInfo.subsectionInfos.filter(
              ({ contentType }) => contentType === RAW_CONTENT_TYPE.QUIZ
            ),
          }))
          .filter(sectionInfo => sectionInfo.subsectionInfos.length > 0)
      : sectionInfos
          .map(sectionInfo => ({
            sectionCode: sectionInfo.sectionCode,
            sectionName: sectionInfo.sectionName,
            sectionGroupInfos: sectionInfo.sectionGroupInfos
              .map(mission => ({
                sectionGroupCode: mission.sectionGroupCode,
                sectionGroupName: mission.sectionGroupName,
                subsectionInfos: mission.subsectionInfos.filter(
                  ({ contentType }) => contentType === RAW_CONTENT_TYPE.QUIZ
                ),
              }))
              .filter(mission => mission.subsectionInfos.length > 0),
          }))
          .filter(sectionInfos => sectionInfos.sectionGroupInfos.length > 0);

  const formattedReportAOA =
    mode === SYLLABUS_MODE.TRADITIONAL
      ? [
          ['', '', '', ''],
          ['', '', '', ''],
          ['', '', '', ''],
          ['ห้อง', 'เลขที่', 'รหัสผู้ใช้', 'ชื่อ-นามสกุล'],
        ]
      : [
          ['', '', '', ''],
          ['', '', '', ''],
          ['', '', '', ''],
          ['', '', '', ''],
          ['ห้อง', 'เลขที่', 'รหัสผู้ใช้', 'ชื่อ-นามสกุล'],
        ];

  formattedReportAOA[0].push(
    mode === SYLLABUS_MODE.TRADITIONAL
      ? filteredSectionInfos[0]?.subsectionInfos[0]?.courseName
      : filteredSectionInfos[0]?.sectionGroupInfos[0]?.subsectionInfos[0]?.courseName
  );

  if (mode === SYLLABUS_MODE.TRADITIONAL) {
    filteredSectionInfos.forEach(({ sectionName, subsectionInfos }) => {
      subsectionInfos.forEach(({ subsectionName }, index) => {
        if (index === 0) {
          if (isAllExam) {
            formattedReportAOA[1].push(sectionName, '', '', '', '', '', '', '');
          } else {
            formattedReportAOA[1].push(sectionName, '', '');
          }
        } else if (isAllExam) {
          formattedReportAOA[1].push('', '', '', '', '', '', '', '');
        } else {
          formattedReportAOA[1].push('', '', '');
        }
        if (isAllExam) {
          formattedReportAOA[2].push(subsectionName, '', '', '', '', '', '', '');
          formattedReportAOA[3].push(
            'คะแนนเต็ม',
            'คะแนนเฉลี่ย',
            'คะแนนดีที่สุด',
            'คะแนนครั้งที่ 1',
            'คะแนนครั้งที่ 2',
            'คะแนนครั้งที่ 3',
            'คะแนนครั้งที่ 4',
            'คะแนนครั้งที่ 5'
          );
        } else {
          formattedReportAOA[2].push(subsectionName, '', '');
          formattedReportAOA[3].push('คะแนนเฉลี่ย', 'คะแนนดีที่สุด', 'คะแนนทั้งหมด');
        }
      });
    });
  } else {
    filteredSectionInfos.forEach(({ sectionName, sectionGroupInfos }) => {
      sectionGroupInfos.forEach(({ sectionGroupName, subsectionInfos }, index) => {
        if (index === 0) {
          if (isAllExam) {
            formattedReportAOA[1].push(sectionName, '', '', '', '', '', '', '');
          } else {
            formattedReportAOA[1].push(sectionName, '', '');
          }
        } else if (isAllExam) {
          formattedReportAOA[1].push('', '', '', '', '', '', '', '');
        } else {
          formattedReportAOA[1].push('', '', '');
        }
        if (isAllExam) {
          formattedReportAOA[2].push(sectionGroupName, '', '', '', '', '', '', '');
          subsectionInfos.forEach(({ subsectionName }) => {
            formattedReportAOA[3].push(subsectionName, '', '', '', '', '', '', '');
            formattedReportAOA[4].push(
              'คะแนนเต็ม',
              'คะแนนเฉลี่ย',
              'คะแนนดีที่สุด',
              'คะแนนครั้งที่ 1',
              'คะแนนครั้งที่ 2',
              'คะแนนครั้งที่ 3',
              'คะแนนครั้งที่ 4',
              'คะแนนครั้งที่ 5'
            );
          });
        } else {
          formattedReportAOA[2].push(sectionGroupName, '', '');
          subsectionInfos.forEach(({ subsectionName }) => {
            formattedReportAOA[3].push(subsectionName, '', '');
            formattedReportAOA[4].push('คะแนนเฉลี่ย', 'คะแนนดีที่สุด', 'คะแนนทั้งหมด');
          });
        }
      });
    });
  }

  const overAllReport = convertOverAllReportToAOA(reports, isAllExam);

  formattedReportAOA.push(...overAllReport);

  return { overAllAOA: formattedReportAOA, overAllHeader: filteredSectionInfos };
};

export const formatProgressToAOA = (data, progressType, mode) => {
  const { sectionInfos, reports } = data;
  const progressTypes = _.castArray(progressType);

  const filteredSectionInfos =
    mode === SYLLABUS_MODE.TRADITIONAL
      ? sectionInfos
          .map(sectionInfo => ({
            sectionCode: sectionInfo.sectionCode,
            sectionName: sectionInfo.sectionName,
            subsectionInfos: sectionInfo.subsectionInfos.filter(({ contentType }) =>
              progressTypes.includes(contentType)
            ),
          }))
          .filter(sectionInfo => sectionInfo.subsectionInfos.length > 0)
      : sectionInfos
          .map(sectionInfo => ({
            sectionCode: sectionInfo.sectionCode,
            sectionName: sectionInfo.sectionName,
            sectionGroupInfos: sectionInfo.sectionGroupInfos
              .map(mission => ({
                sectionGroupCode: mission.sectionGroupCode,
                sectionGroupName: mission.sectionGroupName,
                subsectionInfos: mission.subsectionInfos.filter(({ contentType }) =>
                  progressTypes.includes(contentType)
                ),
              }))
              .filter(mission => mission.subsectionInfos.length > 0),
          }))
          .filter(sectionInfos => sectionInfos.sectionGroupInfos.length > 0);

  const progressLabel = progressTypes.includes(RAW_CONTENT_TYPE.VDO)
    ? 'ความคืบหน้าเฉลี่ย'
    : 'การเข้าสอบเฉลี่ย';

  // Video sheet only: a "% ที่ดู" cell follows each video's status cell.
  const addWatchedPercentColumn = progressTypes.includes(RAW_CONTENT_TYPE.VDO);

  const formattedReportAOA =
    mode === SYLLABUS_MODE.TRADITIONAL
      ? [
          ['', '', '', '', ''],
          ['', '', '', '', ''],
          ['ห้อง', 'เลขที่', 'รหัสผู้ใช้', 'ชื่อ-นามสกุล', `${progressLabel} (เปอร์เซ็นต์)`],
        ]
      : [
          ['', '', '', '', ''],
          ['', '', '', '', ''],
          ['', '', '', '', ''],
          ['ห้อง', 'เลขที่', 'รหัสผู้ใช้', 'ชื่อ-นามสกุล', `${progressLabel} (เปอร์เซ็นต์)`],
        ];

  formattedReportAOA[0].push(
    mode === SYLLABUS_MODE.TRADITIONAL
      ? filteredSectionInfos[0]?.subsectionInfos[0].courseName
      : filteredSectionInfos[0]?.sectionGroupInfos[0]?.subsectionInfos[0]?.courseName
  );

  if (mode === SYLLABUS_MODE.TRADITIONAL) {
    filteredSectionInfos.forEach(({ sectionName, subsectionInfos }) => {
      subsectionInfos.forEach(({ subsectionName }, index) => {
        if (index === 0) {
          formattedReportAOA[1].push(sectionName);
        } else {
          formattedReportAOA[1].push('');
        }
        if (addWatchedPercentColumn) formattedReportAOA[1].push('');

        formattedReportAOA[2].push(subsectionName);
        if (addWatchedPercentColumn) formattedReportAOA[2].push('% ที่ดู');
      });
    });
  } else {
    // One cell per subsection column in every header row, so each section/mission name
    // lands on the first column of its merged range.
    filteredSectionInfos.forEach(({ sectionName, sectionGroupInfos }) => {
      sectionGroupInfos.forEach(({ sectionGroupName, subsectionInfos }, missionIndex) => {
        subsectionInfos.forEach(({ subsectionName }, index) => {
          formattedReportAOA[1].push(missionIndex === 0 && index === 0 ? sectionName : '');
          formattedReportAOA[2].push(index === 0 ? sectionGroupName : '');
          formattedReportAOA[3].push(subsectionName);
          if (addWatchedPercentColumn) {
            formattedReportAOA[1].push('');
            formattedReportAOA[2].push('');
            formattedReportAOA[3].push('% ที่ดู');
          }
        });
      });
    });
  }

  const aoa = convertProgressReportToAOA(reports, progressTypes, {
    includeWatchedPercent: addWatchedPercentColumn,
  });

  formattedReportAOA.push(...aoa);

  return { formattedReportAOA, sectionInfos: filteredSectionInfos, addWatchedPercentColumn };
};

const createOverAllsheet = (overAllAOA, overAllHeader, isAllExam, mode) => {
  let sectionStart = 4;
  let sectionEnd = 0;
  let missionStart = 4;
  let missionEnd = 0;
  let subsectionStart = 4;
  let subsectionEnd = 0;
  let colWidth = isAllExam ? 8 : 3;
  const defaultCols = isAllExam
    ? [{ wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }]
    : [{ wch: 15 }, { wch: 15 }, { wch: 15 }];

  const worksheet = XLSX.utils.aoa_to_sheet(overAllAOA);

  worksheet['!cols'] = [
    { wch: 10 }, //ห้อง
    { wch: 10 }, //เลขที่
    { wch: 15 }, //รหัสผู้ใช้
    { wch: 25 }, //ชื่อ-นามสกุล
  ];

  worksheet['!merges'] = [];

  if (mode === SYLLABUS_MODE.TRADITIONAL) {
    overAllHeader.forEach(({ subsectionInfos }) => {
      subsectionInfos.forEach(() => {
        subsectionEnd = subsectionStart + (isAllExam ? 7 : 2);
        worksheet['!merges'].push({
          s: { r: 2, c: subsectionStart },
          e: { r: 2, c: subsectionEnd },
        });
        subsectionStart = subsectionEnd + 1;
        worksheet['!cols'].push(...defaultCols);
      });
      sectionEnd = sectionStart + subsectionInfos.length * colWidth - 1;
      worksheet['!merges'].push({ s: { r: 1, c: sectionStart }, e: { r: 1, c: sectionEnd } });
      sectionStart = sectionEnd + 1;
    });
  } else {
    overAllHeader.forEach(({ sectionGroupInfos }) => {
      sectionGroupInfos.forEach(({ subsectionInfos }) => {
        missionEnd = (missionStart + (isAllExam ? 7 : 2)) * subsectionInfos.length;
        worksheet['!merges'].push({
          s: { r: 2, c: missionStart },
          e: { r: 2, c: missionEnd },
        });
        missionStart = missionEnd + 1;
        worksheet['!cols'].push(...defaultCols);
        subsectionInfos.forEach(() => {
          subsectionEnd = subsectionStart + (isAllExam ? 7 : 2);
          worksheet['!merges'].push({
            s: { r: 3, c: subsectionStart },
            e: { r: 3, c: subsectionEnd },
          });
          subsectionStart = subsectionEnd + 1;
          worksheet['!cols'].push(...defaultCols);
        });
      });
      sectionEnd = sectionStart + sectionGroupInfos.length * colWidth - 1;
      worksheet['!merges'].push({ s: { r: 1, c: sectionStart }, e: { r: 1, c: sectionEnd } });
      sectionStart = sectionEnd + 1;
    });
  }

  worksheet['!merges'].push({ s: { r: 0, c: 4 }, e: { r: 0, c: subsectionEnd } });

  return worksheet;
};

export const createProgressSheet = (aoa, header, mode, addWatchedPercentColumn = false) => {
  let sectionStart = 5;
  let sectionEnd = 0;
  let missionStart = 5;
  let missionEnd = 0;
  // Each video subsection occupies 2 columns (status + % ที่ดู) instead of 1.
  const colWidth = addWatchedPercentColumn ? 2 : 1;

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  worksheet['!cols'] = [
    { wch: 10 }, //ห้อง
    { wch: 10 }, //เลขที่
    { wch: 15 }, //รหัสผู้ใช้
    { wch: 25 }, //ชื่อ-นามสกุล
  ];

  worksheet['!merges'] = [];

  if (mode === SYLLABUS_MODE.TRADITIONAL) {
    header.forEach(({ subsectionInfos }) => {
      subsectionInfos.forEach(() => {
        worksheet['!cols'].push({ wch: 15 });
        if (addWatchedPercentColumn) worksheet['!cols'].push({ wch: 10 });
      });
      sectionEnd = sectionStart + subsectionInfos.length * colWidth - 1;
      worksheet['!merges'].push({ s: { r: 1, c: sectionStart }, e: { r: 1, c: sectionEnd } });
      sectionStart = sectionEnd + 1;
    });
  } else {
    header.forEach(({ sectionGroupInfos }) => {
      missionStart = sectionStart;
      const sectionSubsectionCount = sectionGroupInfos.reduce(
        (sum, mission) => sum + mission.subsectionInfos.length,
        0
      );
      sectionEnd = sectionStart + sectionSubsectionCount * colWidth - 1;

      worksheet['!merges'].push({
        s: { r: 1, c: sectionStart },
        e: { r: 1, c: sectionEnd },
      });
      sectionGroupInfos.forEach(({ subsectionInfos }) => {
        missionEnd = missionStart + subsectionInfos.length * colWidth - 1;
        worksheet['!merges'].push({
          s: { r: 2, c: missionStart },
          e: { r: 2, c: missionEnd },
        });
        subsectionInfos.forEach(() => {
          worksheet['!cols'].push({ wch: 15 });
          if (addWatchedPercentColumn) worksheet['!cols'].push({ wch: 10 });
        });
        missionStart = missionEnd + 1;
      });
      sectionStart = sectionEnd + 1;
    });
  }

  worksheet['!merges'].push({ s: { r: 0, c: 5 }, e: { r: 0, c: sectionEnd } });

  return worksheet;
};

const ExportSectionReportData = props => {
  const {
    schoolTermId,
    schoolGradeId,
    classNo,
    subjectId,
    courseCode,
    schoolCode,
    sectionCode,
    children,
    mode,
  } = props;
  const client = useApolloClient();

  const fetchSectionReportData = async () => {
    try {
      const { data } = await client.query({
        query:
          mode === SYLLABUS_MODE.TRADITIONAL
            ? getSectionReportForExport
            : getTKSectionReportForExport,
        variables: {
          schoolTermId,
          schoolGradeId,
          classNo,
          subjectId,
          schoolCode,
          courseCode,
        },
      });
      return { data };
    } catch (error) {
      return { error };
    }
  };

  const fetchSectionReportAllExamExportData = async () => {
    try {
      const { data } = await client.query({
        query:
          mode === SYLLABUS_MODE.TRADITIONAL
            ? getSectionReportAllExamForExport
            : getTKSectionReportAllExamForExport,
        variables: {
          schoolTermId,
          schoolGradeId,
          classNo,
          subjectId,
          schoolCode,
          courseCode,
          sectionCode,
        },
      });
      return { data };
    } catch (error) {
      return { error };
    }
  };

  const exportSectionReport = async ({ data, type, isAllExam = false }) => {
    const { reports, subject: subjectObj = {}, schoolTerm: { shortName: schoolTerm } = {} } = data;

    const { name: subject } = subjectObj;

    const headerClassNo = classNo ?? `ทุกห้อง`;

    const { schoolGrade: { shortName: schoolGrade } = {} } = reports[0];

    const headerSchoolGrade = schoolGradeId ? schoolGrade : `ทุกระดับชั้น`;

    const { overAllAOA, overAllHeader } = formatQuizOverAllToAOA(data, isAllExam, mode);
    const overAllSheet = createOverAllsheet(overAllAOA, overAllHeader, isAllExam, mode);

    const {
      formattedReportAOA: videoProgressAOA,
      sectionInfos: videoProgressHeader,
      addWatchedPercentColumn,
    } = formatProgressToAOA(data, RAW_CONTENT_TYPE.VDO, mode);

    const videoProgressSheet = createProgressSheet(
      videoProgressAOA,
      videoProgressHeader,
      mode,
      addWatchedPercentColumn
    );

    const {
      formattedReportAOA: quizProgressAOA,
      sectionInfos: quizProgressHeader,
    } = formatProgressToAOA(data, [RAW_CONTENT_TYPE.QUIZ, RAW_CONTENT_TYPE.QUIZ_FEED], mode);

    const quizProgressSheet = createProgressSheet(quizProgressAOA, quizProgressHeader, mode);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, overAllSheet, 'ภาพรวมคะแนน');
    XLSX.utils.book_append_sheet(workbook, videoProgressSheet, 'ภาพรวมการเข้าเรียน');
    XLSX.utils.book_append_sheet(workbook, quizProgressSheet, 'ภาพรวมการเข้าสอบ');

    const filename = isAllExam
      ? `ข้อมูลรายบท_${formatSchoolTerm(
          schoolTerm
        )}_${headerSchoolGrade}-${headerClassNo}_${subject}_full`
      : `ข้อมูลรายบท_${formatSchoolTerm(
          schoolTerm
        )}_${headerSchoolGrade}-${headerClassNo}_${subject}`;
    XLSX.writeFile(workbook, `${filename}.${type || 'xlsx'}`);
  };

  return children({
    fetchSectionReportData,
    fetchSectionReportAllExamExportData,
    exportSectionReport,
  });
};

export default ExportSectionReportData;
